import { Button, Divider, Flex, Form, Input, message, Modal, Select, theme } from 'antd';
import { useCreateExternalAccount } from './useCreateExternalAccount';
import { FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCountryOptions } from '../../libs/countries';
import { filterOption } from '../../libs/form/filterOption';

export const CreateExternalAccountModal: FC<{
  customerId: string;
  open: boolean;
  onClose: () => void;
}> = ({ onClose, open, customerId }) => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const mutation = useCreateExternalAccount();
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();

  const onFinish = (values: {
    account_holder_type: 'individual' | 'company';
    account_holder_first_name?: string;
    account_holder_last_name?: string;
    account_holder_business_name?: string;
    bank_name: string;
    account_type: string;
    'account_holder_address.country': string;
    'account_holder_address.city': string;
    'account_holder_address.street_line_1': string;
    'account_holder_address.postal_code': string;
    'iban.iban_number'?: string;
    'iban.bic'?: string;
    'us.account_number'?: string;
    'us.routing_number'?: string;
    'international_wire.account_number'?: string;
    'international_wire.routing_number'?: string;
    'international_wire.bic'?: string;
  }) => {
    mutation
      .mutateAsync({
        customerId,
        payload: {
          bank_name: values.bank_name,
          account_holder_type: values.account_holder_type,
          account_holder_first_name: values.account_holder_first_name || null,
          account_holder_last_name: values.account_holder_last_name || null,
          account_holder_business_name: values.account_holder_business_name || null,
          account_type: values.account_type,
          account_holder_address: {
            country: values['account_holder_address.country'],
            city: values['account_holder_address.city'],
            street_line_1: values['account_holder_address.street_line_1'],
            postal_code: values['account_holder_address.postal_code'],
          },
          ...(values['iban.iban_number'] && {
            iban: {
              iban_number: values['iban.iban_number'],
              bic: values['iban.bic']!,
            },
          }),
          ...(values['us.account_number'] && {
            us: {
              account_number: values['us.account_number'],
              routing_number: values['us.routing_number']!,
            },
          }),
          ...(values['international_wire.account_number'] && {
            international_wire: {
              account_number: values['international_wire.account_number'],
              routing_number: values['international_wire.routing_number']!,
              bic: values['international_wire.bic']!,
            },
          }),
        },
      })
      .then(() => {
        messageApi.success('Success!');
        onClose();
        return queryClient.invalidateQueries({
          queryKey: ['external-accounts'],
        });
      })
      .catch((err) => {
        messageApi.error(
          err?.response?.status === 424 ? `Error: ${err?.response?.data?.error}` : 'Something is wrong',
          6000
        );
      });
  };

  return (
    <>
      {contextHolder}
      <Modal
        width={580}
        loading={mutation.isPending}
        title="Add External Account"
        destroyOnClose
        footer={null}
        open={open}
        onCancel={onClose}
      >
        <Form
          onFinish={onFinish}
          name="addExternalAccount"
          form={form}
          initialValues={{
            account_holder_type: 'individual',
          }}
          requiredMark={false}
          style={{
            paddingTop: 20,
          }}
          layout="vertical"
          autoComplete="off"
        >
          <Divider style={{ color: token.colorTextQuaternary, fontSize: '14px' }} variant="dashed">
            Account Holder Name
          </Divider>

          <Form.Item
            rules={[
              {
                required: true,
                message: 'Please select',
              },
            ]}
            name="account_holder_type"
            layout="vertical"
            label="Type"
          >
            <Select placeholder="Select value...">
              <Select.Option value="individual">Individual</Select.Option>
              <Select.Option value="business">Business</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item shouldUpdate={true} noStyle>
            {() => {
              const customerType = form.getFieldValue('account_holder_type');

              if (!customerType) {
                return null;
              }

              if (customerType === 'individual') {
                return (
                  <Flex gap={'middle'}>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="account_holder_first_name"
                      layout="vertical"
                      label="First Name"
                    >
                      <Input placeholder="e.g. John" />
                    </Form.Item>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="account_holder_last_name"
                      layout="vertical"
                      label="Last Name"
                    >
                      <Input placeholder="e.g. Doe" />
                    </Form.Item>
                  </Flex>
                );
              }

              return (
                <>
                  <Form.Item
                    rules={[
                      {
                        required: true,
                        message: 'Please enter',
                      },
                    ]}
                    name="account_holder_business_name"
                    layout="vertical"
                    label="Business Name"
                  >
                    <Input placeholder="e.g. Acme Ltd" />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>

          <Divider style={{ color: token.colorTextQuaternary, fontSize: '14px' }} variant="dashed">
            Account Number
          </Divider>

          <Flex gap={'middle'}>
            <Form.Item
              style={{ flex: 1 }}
              rules={[
                {
                  required: true,
                  message: 'Please select',
                },
              ]}
              name="account_type"
              layout="vertical"
              label="Account Type"
            >
              <Select placeholder="Select value...">
                <Select.Option value="iban">IBAN</Select.Option>
                <Select.Option value="international_wire">International Wire</Select.Option>
                <Select.Option value="us">US</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              rules={[
                {
                  required: true,
                  message: 'Please enter',
                },
              ]}
              name="bank_name"
              layout="vertical"
              label="Bank Name"
            >
              <Input placeholder="Lead Bank" />
            </Form.Item>
          </Flex>

          <Form.Item shouldUpdate={true} noStyle>
            {() => {
              const type = form.getFieldValue('account_type');

              if (!type) {
                return null;
              }

              if (type === 'iban') {
                return (
                  <Flex gap={'middle'}>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="iban.iban_number"
                      layout="vertical"
                      label="IBAN Number"
                    >
                      <Input placeholder="e.g. GB82WEST12345698765432" />
                    </Form.Item>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="iban.bic"
                      layout="vertical"
                      label="BIC/SWIFT Code"
                    >
                      <Input placeholder="e.g. WESTGB22" />
                    </Form.Item>
                  </Flex>
                );
              }

              if (type === 'international_wire') {
                return (
                  <Flex gap={'middle'}>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="international_wire.account_number"
                      layout="vertical"
                      label="Account Number"
                    >
                      <Input placeholder="e.g. 123456789" />
                    </Form.Item>
                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="international_wire.routing_number"
                      layout="vertical"
                      label="Routing Number"
                    >
                      <Input placeholder="e.g. 021000021" />
                    </Form.Item>

                    <Form.Item
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: 'Please enter',
                        },
                      ]}
                      name="international_wire.bic"
                      layout="vertical"
                      label="BIC/SWIFT Code"
                    >
                      <Input placeholder="e.g. WESTGB22" />
                    </Form.Item>
                  </Flex>
                );
              }

              return (
                <Flex gap={'middle'}>
                  <Form.Item
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter',
                      },
                    ]}
                    name="us.account_number"
                    layout="vertical"
                    label="Account Number"
                  >
                    <Input placeholder="e.g. 123456789" />
                  </Form.Item>
                  <Form.Item
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: 'Please enter',
                      },
                    ]}
                    name="us.routing_number"
                    layout="vertical"
                    label="Routing Number"
                  >
                    <Input placeholder="e.g. 021000021" />
                  </Form.Item>
                </Flex>
              );
            }}
          </Form.Item>

          <Divider style={{ color: token.colorTextQuaternary, fontSize: '14px' }} variant="dashed">
            Account Holder Address
          </Divider>

          <Flex gap={'middle'}>
            <Form.Item style={{ flex: 1 }} name="account_holder_address.country" layout="vertical" label="Country">
              <Select
                showSearch
                placeholder={'Select country'}
                filterOption={filterOption}
                options={getCountryOptions()}
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1 }}
              name="account_holder_address.street_line_1"
              layout="vertical"
              label="Street Line"
            >
              <Input />
            </Form.Item>
          </Flex>

          <Flex gap={'middle'}>
            <Form.Item name="account_holder_address.city" style={{ flex: 1 }} layout="vertical" label="City">
              <Input />
            </Form.Item>

            <Form.Item
              name="account_holder_address.postal_code"
              style={{ flex: 1 }}
              layout="vertical"
              label="Postal Code"
            >
              <Input />
            </Form.Item>
          </Flex>

          <Flex style={{ paddingTop: '18px' }} gap={'middle'} justify="flex-end">
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Add
            </Button>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
